/**
 * ActionItemsPanel Component
 *
 * Displays prioritized actionable items requiring guide attention.
 * Shows unread messages, pending booking requests, reviews awaiting response,
 * upcoming trips, profile completion, and expiring credentials.
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: SCRUM-01: Action Items Display (3 SP)
 *
 * Props:
 * - actionItems: array of action items from API
 * - loading: boolean
 * - error: string
 * - onNavigate: function(url) to handle navigation
 */

import {
  MessageSquare,
  Calendar,
  Star as _Star,
  MapPin as _MapPin,
  User as _User,
  FileText,
  AlertCircle as _AlertCircle,
  ChevronRight,
  CheckCircle,
} from 'lucide-react';

/**
 * Get icon component based on action item type
 */
function getActionIcon(type) {
  const icons = {
    unread_messages: MessageSquare,
    pending_booking: Calendar,
    review_response: _Star,
    upcoming_trip: _MapPin,
    profile_incomplete: _User,
    credential_expiring: FileText,
  };
  return icons[type] || _AlertCircle;
}

/**
 * Get priority configuration (color, urgency indicator)
 */
function getPriorityConfig(priority) {
  const configs = {
    urgent: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-600',
      badge: 'bg-red-600 text-white',
      badgeText: 'Urgent',
    },
    high: {
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-800',
      iconColor: 'text-orange-600',
      badge: 'bg-orange-600 text-white',
      badgeText: 'High',
    },
    medium: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-600',
      badge: 'bg-yellow-600 text-white',
      badgeText: 'Medium',
    },
    low: {
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-800',
      iconColor: 'text-gray-600',
      badge: 'bg-gray-600 text-white',
      badgeText: 'Low',
    },
  };
  return configs[priority] || configs.medium;
}

export default function ActionItemsPanel({ actionItems = [], loading = false, error = null, onNavigate }) {
  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-2">
          <_AlertCircle className="w-6 h-6 text-red-600" />
          <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!actionItems || actionItems.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mb-3" />
          <p className="text-gray-600 font-medium">All caught up!</p>
          <p className="text-sm text-gray-500 mt-1">You have no pending action items at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <_AlertCircle className="w-6 h-6 text-rose-600" />
            <h2 className="text-lg font-semibold text-gray-900">Action Items</h2>
            <span className="px-2.5 py-0.5 bg-rose-100 text-rose-700 text-sm font-medium rounded-full">
              {actionItems.length}
            </span>
          </div>
        </div>
      </div>

      {/* Action Items List */}
      <div className="divide-y divide-gray-200">
        {actionItems.map((item) => {
          const Icon = getActionIcon(item.type);
          const priorityConfig = getPriorityConfig(item.priority);

          return (
            <div
              key={item.id}
              className={`px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${priorityConfig.bgColor} border-l-4 ${priorityConfig.borderColor}`}
              onClick={() => onNavigate && onNavigate(item.action_url)}
            >
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className={`flex-shrink-0 mt-0.5`}>
                  <Icon className={`w-5 h-5 ${priorityConfig.iconColor}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className={`text-sm font-semibold ${priorityConfig.textColor}`}>
                      {item.title}
                    </h3>
                    {(item.priority === 'urgent' || item.priority === 'high') && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${priorityConfig.badge}`}>
                        {priorityConfig.badgeText}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{item.description}</p>
                  {item.action_text && (
                    <div className="mt-2">
                      <button
                        className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                        onClick={(  _e) => {
                          _e.stopPropagation();
                          onNavigate && onNavigate(item.action_url);
                        }}
                      >
                        {item.action_text}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Count Badge (for messages) */}
                {item.count && item.count > 0 && (
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${priorityConfig.badge}`}>
                      {item.count}
                    </span>
                  </div>
                )}

                {/* Arrow Icon */}
                {!item.count && (
                  <div className="flex-shrink-0">
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
